import { Box, Flex, Tag } from '@chakra-ui/react';
import {
  Dropdown,
  DropdownChip,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { useDispatch, useSelector } from 'react-redux';
import { updateSelectedTimelines } from 'reducers/okrsPageSlice';
import { setTimeBoundsToLS } from 'screens/Team';
import { useGoalAnalytics } from 'screens/analytics/goals/GoalAnalyticsContext';
import { okrsPageSelector } from 'selectors/okrsPage';

import { getTimePeriodIdString } from 'constants/helper';
import { sendClickedOnTimePeriodEvent } from 'constants/mixpanelHelper';

import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

export const GoalsIntentTimelineDropdown = ({ timelineDropdownOnChangeCallback }) => {
  const { timelinesData, allTimelines, selectedTimelines } = useSelector(okrsPageSelector);

  const { previous, current, next, other = [] } = timelinesData ?? {};
  const previousCurrentNext = [previous, current, next].filter((item) => Boolean(item));
  const allTimePeriods = [previous, current, next, ...other].filter((item) => Boolean(item));

  const selectedTimeBoundPeriod = selectedTimelines?.[0] ?? null;
  const otherMenuItems = other.map(({ startTS, endTS, timelineTag }) => ({
    value: getTimePeriodIdString({ timelineTag, startTS, endTS }),
    label: timelineTag,
  }));
  const currentSelectedValue = getTimePeriodIdString(selectedTimeBoundPeriod);

  const { updateTimeBoundPeriods } = useGoalAnalytics();
  const dispatch = useDispatch();

  const menuItems = previousCurrentNext.map((timeBound) => {
    const { timelineTag, startTS, endTS } = timeBound;
    return {
      value: getTimePeriodIdString({ timelineTag, startTS, endTS }),
      label: timelineTag,
      tag: timeBound === current ? 'Current' : '',
    };
  });

  const onTimePeriodChange = (timePeriod) => {
    sendClickedOnTimePeriodEvent({
      eventProperties: {
        timePeriod: timePeriod?.timelineTag,
      },
    });
    dispatch(updateSelectedTimelines([timePeriod]));
    updateTimeBoundPeriods(timePeriod);

    setTimeBoundsToLS([timePeriod]);
  };

  //   if allTimelines or selectedTimelines is null or length is 0 then return null

  if (!allTimelines || !selectedTimelines || allTimelines?.length === 0 || selectedTimelines?.length === 0) {
    return null;
  }

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <DropdownChip>
          <TextBodyMedium bold color="text.g2" className="group-data[state=open]:text-[#004F67]">
            Timeline :
          </TextBodyMedium>
          <TextBodyMedium bold color="text.g1">
            {selectedTimelines[0]?.timelineTag}
          </TextBodyMedium>
        </DropdownChip>
      </DropdownTrigger>
      <DropdownContent
        align="start"
        style={{
          maxHeight: '250px',
          overflowY: 'auto',
          minWidth: '220px',
        }}
      >
        <DropdownRadioGroup value={currentSelectedValue}>
          {menuItems.map((item) => (
            <DropdownRadioItem
              key={item.value}
              value={item.value}
              onClick={() => {
                if (typeof timelineDropdownOnChangeCallback === 'function') {
                  timelineDropdownOnChangeCallback(() =>
                    onTimePeriodChange(allTimePeriods.find((t) => t.timelineTag === item.label))
                  );
                } else {
                  onTimePeriodChange(allTimePeriods.find((t) => t.timelineTag === item.label));
                }
              }}
            >
              <Flex alignItems="center" w="100%">
                {item.label}
                {item.tag && (
                  <Tag ml="auto" marginLeft="auto">
                    Current
                  </Tag>
                )}
              </Flex>
            </DropdownRadioItem>
          ))}

          <Box padding="8px">
            <TextLabel bold color="text.g2">
              Other Time Periods
            </TextLabel>
          </Box>

          {otherMenuItems.map((item) => (
            <DropdownRadioItem
              key={item.value}
              value={item.value}
              onClick={() => onTimePeriodChange(allTimePeriods.find((t) => t.timelineTag === item.label))}
            >
              {item.label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      </DropdownContent>
    </Dropdown>
  );
};
